import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import Sidebar from "../../components/sidebar/Sidebar";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import KpiCard from "../../components/commercial-dashboard/KpiCard";
import {
  KpiGrid,
  Section,
} from "../../components/commercial-dashboard/DashboardBits";
import {
  Caret,
  ChannelSwatch,
  Note,
  Num,
} from "../../components/control-board/ControlBoardBits";
import {
  MONTHS_SHORT,
  eur,
  groupBy,
  sum,
} from "../../components/control-board/controlBoardModel";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { useUserRole } from "../../hooks/useUserRole";
import { useGetCompaniesQuery } from "../../store/apis/companyApi";
import {
  useActivateBusinessPlanImportMutation,
  useDeleteBusinessPlanImportMutation,
  useGetBusinessPlanQuery,
  useImportBusinessPlanMutation,
  useSetBusinessPlanMappingMutation,
} from "../../store/apis/controlBoardApi";
import {
  BusinessPlanLineView,
  BusinessPlanPairView,
} from "../../types/controlBoard";
import { CONTROL_BOARD_ROLES } from "./ControlBoard";

type Metric =
  | "managedInvestment"
  | "invoicedInvestment"
  | "campaignProfit"
  | "fee"
  | "totalProfit";

const METRICS: { value: Metric; label: string }[] = [
  { value: "managedInvestment", label: "Inversión gestionada" },
  { value: "invoicedInvestment", label: "Inversión facturada" },
  { value: "campaignProfit", label: "Beneficio campaña" },
  { value: "fee", label: "Fee" },
  { value: "totalProfit", label: "Beneficio total (margen + fee)" },
];

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const apiMessage = (error: unknown): string | null =>
  error && typeof error === "object" && "data" in error
    ? ((error as { data?: { message?: string } }).data?.message ?? null)
    : null;

/**
 * Business Plan del año: se sube desde aquí (Excel, CSV o la tabla en Markdown de la hoja),
 * se consulta por cliente, marca, formato y mes, y se empareja cada cliente + marca con su
 * empresa para que el tablero de control lo pueda comparar con lo real.
 */
const BusinessPlan: React.FC = () => {
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { role } = useUserRole();
  const canSee = !!role && CONTROL_BOARD_ROLES.includes(role);
  const [search, setSearch] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const year = Number(search.get("year")) || currentYear;
  const fileRef = useRef<HTMLInputElement>(null);

  const [house, setHouse] = useState("");
  const [client, setClient] = useState("");
  const [metric, setMetric] = useState<Metric>("managedInvestment");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [toast, setToast] = useState<{
    severity: "success" | "error" | "warning";
    text: string;
  } | null>(null);

  const { data, isLoading, error } = useGetBusinessPlanQuery(year, {
    skip: !canSee,
  });
  const plan = data?.data ?? null;
  const { data: companiesData } = useGetCompaniesQuery(undefined, {
    skip: !canSee,
  });
  const companies = useMemo(
    () =>
      (companiesData?.data ?? [])
        .map((c) => ({
          id: c.id,
          label: c.nameCommercial || c.nameFiscal || c.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [companiesData]
  );

  const [importPlan, { isLoading: importing }] =
    useImportBusinessPlanMutation();
  const [activate] = useActivateBusinessPlanImportMutation();
  const [remove] = useDeleteBusinessPlanImportMutation();
  const [setMapping] = useSetBusinessPlanMappingMutation();

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const contentBase64 = await readAsBase64(file);
      const result = await importPlan({
        year,
        fileName: file.name,
        contentBase64,
      }).unwrap();
      const unmatched = result.data?.unmatchedPairs ?? 0;
      setToast({
        severity: unmatched ? "warning" : "success",
        text: `Business Plan ${year} subido: ${result.data?.import.lines ?? 0} filas.${
          unmatched
            ? ` ${unmatched} parejas cliente + marca sin emparejar: emparéjalas abajo.`
            : ""
        }`,
      });
    } catch (e) {
      setToast({
        severity: "error",
        text: apiMessage(e) ?? "No se ha podido subir el archivo.",
      });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const lines = plan?.lines ?? [];
  const houses = [...new Set(lines.map((l) => l.house))].sort();
  const filtered = lines.filter(
    (l) => (!house || l.house === house) && (!client || l.clientName === client)
  );
  const clientsList = [
    ...new Set(
      lines.filter((l) => !house || l.house === house).map((l) => l.clientName)
    ),
  ].sort((a, b) => a.localeCompare(b, "es"));
  const pairs = (plan?.pairs ?? []).filter(
    (p) => !onlyUnmapped || !p.companyId
  );
  const unmapped = (plan?.pairs ?? []).filter((p) => !p.companyId).length;

  const saveMapping = async (
    pair: BusinessPlanPairView,
    companyId: string | null
  ) => {
    try {
      await setMapping({
        clientName: pair.clientName,
        brand: pair.brand,
        companyId,
      }).unwrap();
    } catch (e) {
      setToast({
        severity: "error",
        text: apiMessage(e) ?? "No se ha podido guardar el emparejamiento.",
      });
    }
  };

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const value = (l: BusinessPlanLineView) => l[metric];
  const monthCells = (list: BusinessPlanLineView[], bold = false) => {
    const by = groupBy(list, (l) => l.month);
    return [
      ...months.map((m) => {
        const v = sum(by.get(m) ?? [], value);
        return (
          <Num key={m} muted={!v} sx={{ fontWeight: bold ? 600 : undefined }}>
            {v ? eur(v) : "·"}
          </Num>
        );
      }),
      <Num key="t" sx={{ fontWeight: 700 }}>
        {eur(sum(list, value))}
      </Num>,
    ];
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />
        {!canSee ? (
          <Alert severity="info">
            El Business Plan solo está disponible para Admin y Pricing.
          </Alert>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[200]}`,
                p: { xs: 2, md: 3 },
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                gap={2}
                alignItems={{ sm: "center" }}
              >
                <Box>
                  <Typography variant="h5">Business Plan {year}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Lo que se espera facturar y ganar con cada cliente, marca,
                    formato y mes. Es la referencia del tablero de control. Se
                    sube en Excel, CSV o la tabla en Markdown de la hoja; cada
                    subida es una versión nueva y queda activa.
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  gap={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel id="bp-year">Año</InputLabel>
                    <Select
                      labelId="bp-year"
                      label="Año"
                      value={year}
                      onChange={(e) =>
                        setSearch(
                          { year: String(e.target.value) },
                          { replace: true }
                        )
                      }
                    >
                      {[currentYear + 1, currentYear, currentYear - 1].map(
                        (y) => (
                          <MenuItem key={y} value={y}>
                            {y}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                  <input
                    ref={fileRef}
                    type="file"
                    hidden
                    accept=".xlsx,.csv,.md,.txt"
                    data-testid="bp-file"
                    onChange={(e) => onFile(e.target.files?.[0])}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={
                      importing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <UploadFileIcon />
                      )
                    }
                    disabled={importing}
                    onClick={() => fileRef.current?.click()}
                  >
                    Subir Business Plan
                  </Button>
                  <Button
                    component={RouterLink}
                    to={`/control-board?year=${year}`}
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                  >
                    Tablero de control
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {isLoading && <CircularProgress sx={{ alignSelf: "center" }} />}
            {error && (
              <Alert severity="error">
                {apiMessage(error) ??
                  "No se ha podido cargar el Business Plan."}
              </Alert>
            )}

            {plan && !plan.activeImport && (
              <Alert severity="info">
                Todavía no hay Business Plan para {year}. Súbelo con el botón de
                arriba: tiene que llevar las columnas Cliente, Marca, Mes,
                Formato e Inversión Gestionada (y, si las tiene, Quién Paga,
                Inversión Facturada, Margen, Beneficio Campaña, Fee...).
              </Alert>
            )}

            {plan?.activeImport && (
              <>
                <Section
                  title="Versiones"
                  subtitle="La activa es la que usa el tablero de control. Se puede volver a una anterior."
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Archivo</TableCell>
                        <TableCell>Subido</TableCell>
                        <TableCell>Por</TableCell>
                        <Num>Filas</Num>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {plan.imports.map((imp) => (
                        <TableRow key={imp.id}>
                          <TableCell>
                            {imp.fileName}{" "}
                            {imp.isActive && (
                              <Chip
                                size="small"
                                color="success"
                                label="Activa"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(imp.createdAt).toLocaleString("es-ES")}
                          </TableCell>
                          <TableCell>{imp.uploadedBy ?? "—"}</TableCell>
                          <Num>{imp.lines}</Num>
                          <TableCell align="right">
                            {!imp.isActive && (
                              <Stack
                                direction="row"
                                gap={1}
                                justifyContent="flex-end"
                              >
                                <Button
                                  size="small"
                                  onClick={() => activate(imp.id)}
                                >
                                  Activar
                                </Button>
                                <Tooltip title="Borrar esta versión">
                                  <IconButton
                                    size="small"
                                    aria-label="Borrar versión"
                                    onClick={() => remove(imp.id)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Section>

                <KpiGrid columns={5}>
                  {METRICS.map((m) => (
                    <KpiCard
                      key={m.value}
                      label={m.label}
                      value={eur(sum(filtered, (l) => l[m.value]))}
                      caption={
                        house || client
                          ? "Con los filtros de abajo"
                          : `Todo ${year}`
                      }
                    />
                  ))}
                </KpiGrid>

                <Section
                  title="Emparejamiento con empresas"
                  subtitle="Cada cliente + marca del plan tiene que estar unido a su empresa de la Intelligence para compararlo con lo real. Al subir el plan se emparejan solas las que se reconocen; el resto, aquí."
                  action={
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={onlyUnmapped}
                          onChange={(e) => setOnlyUnmapped(e.target.checked)}
                        />
                      }
                      label={`Solo sin emparejar (${unmapped})`}
                    />
                  }
                >
                  {unmapped > 0 && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      {unmapped}{" "}
                      {unmapped === 1 ? "pareja no tiene" : "parejas no tienen"}{" "}
                      empresa y no entran en las comparaciones del tablero.
                    </Alert>
                  )}
                  <TableContainer sx={{ maxHeight: "60vh" }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Cliente (razón social)</TableCell>
                          <TableCell>Marca</TableCell>
                          <Num>Inversión gestionada</Num>
                          <TableCell sx={{ minWidth: 300 }}>Empresa</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pairs.map((p) => (
                          <TableRow key={`${p.clientName}|${p.brand}`}>
                            <TableCell>{p.clientName}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {p.brand}
                            </TableCell>
                            <Num>{eur(p.managedInvestment)}</Num>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={companies}
                                value={
                                  companies.find((c) => c.id === p.companyId) ??
                                  null
                                }
                                onChange={(_, option) =>
                                  saveMapping(p, option?.id ?? null)
                                }
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Sin emparejar"
                                    error={!p.companyId}
                                    inputProps={{
                                      ...params.inputProps,
                                      "aria-label": `Empresa de ${p.brand}`,
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Section>

                <Section
                  title="El plan por cliente y mes"
                  subtitle="Pulsa un cliente para ver cada marca y formato."
                  action={
                    <Stack direction="row" gap={1.5} flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="bp-house">House</InputLabel>
                        <Select
                          labelId="bp-house"
                          label="House"
                          value={house}
                          onChange={(e) => setHouse(e.target.value)}
                        >
                          <MenuItem value="">Todas</MenuItem>
                          {houses.map((h) => (
                            <MenuItem key={h} value={h}>
                              {h}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="bp-client">Cliente</InputLabel>
                        <Select
                          labelId="bp-client"
                          label="Cliente"
                          value={client}
                          onChange={(e) => setClient(e.target.value)}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          {clientsList.map((c) => (
                            <MenuItem key={c} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="bp-metric">Cifra</InputLabel>
                        <Select
                          labelId="bp-metric"
                          label="Cifra"
                          value={metric}
                          onChange={(e) => setMetric(e.target.value as Metric)}
                        >
                          {METRICS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  }
                >
                  <TableContainer sx={{ maxHeight: "75vh" }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 260 }}>
                            Cliente · marca · formato
                          </TableCell>
                          {months.map((m) => (
                            <Num key={m}>{MONTHS_SHORT[m]}</Num>
                          ))}
                          <Num>Total</Num>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...groupBy(filtered, (l) => l.clientName).entries()]
                          .sort((a, b) => sum(b[1], value) - sum(a[1], value))
                          .map(([name, list]) => {
                            const isOpen = open.has(name);
                            return (
                              <React.Fragment key={name}>
                                <TableRow
                                  hover
                                  sx={{ cursor: "pointer" }}
                                  onClick={() =>
                                    setOpen((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(name)) next.delete(name);
                                      else next.add(name);
                                      return next;
                                    })
                                  }
                                >
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    <Caret open={isOpen} />
                                    {name}
                                  </TableCell>
                                  {monthCells(list, true)}
                                </TableRow>
                                {isOpen &&
                                  [
                                    ...groupBy(
                                      list,
                                      (l) => `${l.brand}|${l.format}`
                                    ).entries(),
                                  ]
                                    .sort((a, b) =>
                                      a[0].localeCompare(b[0], "es")
                                    )
                                    .map(([key, l]) => (
                                      <TableRow
                                        key={key}
                                        sx={{
                                          backgroundColor:
                                            theme.palette.grey[50],
                                        }}
                                      >
                                        <TableCell sx={{ pl: 6 }}>
                                          <ChannelSwatch
                                            channel={l[0].channel}
                                          />
                                          {l[0].brand} · {l[0].format}{" "}
                                          <Typography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {l[0].payer === "CLIENTE"
                                              ? "paga el cliente"
                                              : ""}
                                            {l[0].marginPct
                                              ? ` · margen ${Math.round(l[0].marginPct)} %`
                                              : ""}
                                          </Typography>
                                        </TableCell>
                                        {monthCells(l)}
                                      </TableRow>
                                    ))}
                              </React.Fragment>
                            );
                          })}
                        <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                          <TableCell>Total</TableCell>
                          {monthCells(filtered, true)}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Note>
                    Cada formato del plan se compara en el tablero con un canal:
                    SEM con Google Ads; Meta, TikTok, Pinterest, LinkedIn y
                    gestión de RRSS con Social Ads; Display Rich Media con Rich
                    Media; Vídeo YTB con YouTube; CTV, Amazon Prime y Netflix
                    con Connected TV; Spotify con Audio. El tablero solo usa
                    House of Media.
                  </Note>
                </Section>
              </>
            )}
          </>
        )}
      </Box>
      {toast && (
        <Snackbar open autoHideDuration={8000} onClose={() => setToast(null)}>
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.text}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default BusinessPlan;
